import React, { useState } from "react";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { TotpEnrolmentWizard } from "./TotpEnrolmentWizard";

export interface RecoveryReenrolModalProps {
	isOpen: boolean;
	isUserReEnrol?: boolean;
	/** Dismiss the prompt (either "not now" or after successful re-enrolment). */
	onClose: () => void;
}

/**
 * Shown after a recovery-code login. Offers to re-register a fresh authenticator
 * (the old device is presumed lost). Dismissable — the user can defer via
 * "Not now" and re-enrol later from their profile. Choosing to set up runs the
 * enrolment wizard in "replace" mode (clears the old factor, issues a new secret).
 */
export const RecoveryReenrolModal = ({ isOpen, isUserReEnrol, onClose }: RecoveryReenrolModalProps) => {
	const [started, setStarted] = useState(false);

	const handleClose = () => {
		setStarted(false);
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			closeOnEsc
			isCentered
			size="lg"
			iconType="warning"
			title="Set up a new authenticator?"
			description={
				started
					? undefined
					: `${isUserReEnrol ? "" : "You signed in using a recovery code."} If you've lost access to your authenticator app, set up a new one now to keep your account secure.${isUserReEnrol ? "" : " You can only use each recovery code once."}`
			}
			customFooter={
				started ? undefined : (
					<div className="flex justify-end gap-3 px-6 pb-6">
						<Button
							type="button"
							btntype="secondary"
							onClick={handleClose}
							data-testid="reenrol-not-now-btn"
						>
							Not now
						</Button>
						<Button
							type="button"
							btntype="primary"
							onClick={() => setStarted(true)}
							data-testid="reenrol-setup-btn"
						>
							Set up new authenticator
						</Button>
					</div>
				)
			}
		>
			{started ? (
				<div className="px-6" data-testid="reenrol-wizard">
					<TotpEnrolmentWizard
						mode="replace"
						onAlreadyEnabled={handleClose}
						onComplete={handleClose}
					/>
				</div>
			) : (
				<span />
			)}
		</Modal>
	);
};
